/**
 * Bureau (Cockpit) — PSI Phase 7.5.
 *
 * Centre de travail indépendant. Architecture "Bureau à distance" :
 *  1. Sélecteur de Master Client (dropdown avec recherche)
 *  2. État vide → 4 outils grisés + message d'attente
 *  3. Master sélectionné → 4 cartes Workspace (Drive · Calendar · Docs · Keep)
 *     scoped à l'expertise active (Fiscal / Immeuble)
 *
 * Règle stricte : les outils Workspace n'apparaissent JAMAIS dans la fiche client.
 * La fiche client est réservée à la gestion (activations, permissions, facturation).
 *
 * Batch #14.221 — refactor oversized 2026-02 : sous-composants extraits dans ./bureau/
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Briefcase, Search, FileText, Building2,
  Loader2, X, ChevronDown,
} from "lucide-react";
import { toast } from "@/lib/toast";
import EmptyState from "./bureau/EmptyState";
import Cockpit from "./bureau/Cockpit";
import { API, authH } from "@/api";


const EXPERTISES = [
  { id: "fiscal", label: "Fiscal", icon: FileText, tone: "blue" },
  { id: "immeuble", label: "Immeuble", icon: Building2, tone: "emerald" },
];

export default function Bureau() {
  const [masters, setMasters] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [search, setSearch] = useState("");
  const [showPicker, setShowPicker] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [master, setMaster] = useState(null);
  const [activeExp, setActiveExp] = useState("fiscal");
  const [hub, setHub] = useState(null);
  const [busy, setBusy] = useState(false);

  // Chargement de la liste des masters
  useEffect(() => {
    (async () => {
      setLoadingMasters(true);
      try {
        const r = await axios.get(`${API}/masters`, { headers: authH() });
        setMasters(r.data?.items || []);
      } catch {
        toast.error("Erreur chargement dossiers");
      } finally {
        setLoadingMasters(false);
      }
    })();
  }, []);

  // Sélection d'un master → fetch détails + hub initial
  const loadHub = useCallback(async (id, expertise) => {
    try {
      const r = await axios.get(`${API}/masters/${id}/hub/${expertise}`, { headers: authH() });
      setHub(r.data);
    } catch {
      setHub(null);
    }
  }, []);

  const loadMaster = useCallback(async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/masters/${id}`, { headers: authH() });
      setMaster(r.data);
      const activeOnes = EXPERTISES.filter(
        (e) => r.data?.expertises?.[e.id]?.activated,
      );
      const defaultExp = activeOnes[0]?.id || "fiscal";
      setActiveExp(defaultExp);
      await loadHub(id, defaultExp);
    } catch (e) {
      toast.error("Dossier introuvable");
    } finally {
      setBusy(false);
    }
  }, [loadHub]);

  useEffect(() => {
    if (selectedId) loadMaster(selectedId);
  }, [selectedId, loadMaster]);

  useEffect(() => {
    if (selectedId && activeExp) loadHub(selectedId, activeExp);
  }, [activeExp, selectedId, loadHub]);

  const filteredMasters = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return masters;
    return masters.filter((m) =>
      (m.couple_display_name || "").toLowerCase().includes(q) ||
      (m.primary_email || "").toLowerCase().includes(q) ||
      (m.primary_name || "").toLowerCase().includes(q)
    );
  }, [masters, search]);

  const createCalendarManually = async () => {
    if (!master || !hub) return;
    if (!window.confirm(`Créer un Google Calendar pour l'expertise « ${activeExp} » ?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/masters/${master.id}/expertises/${activeExp}/create-calendar`,
        {}, { headers: authH() },
      );
      setMaster(r.data);
      await loadHub(master.id, activeExp);
      toast.success("Google Calendar créé ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création calendrier");
    } finally {
      setBusy(false);
    }
  };

  const clearSelection = () => {
    setSelectedId(null);
    setMaster(null);
    setHub(null);
    setShowPicker(false);
    setSearch("");
  };

  const expertiseTabs = useMemo(() => {
    if (!master) return [];
    return EXPERTISES.filter((e) => master.expertises?.[e.id]?.activated);
  }, [master]);

  return (
    <div className="space-y-5" data-testid="admin-bureau">
      {/* Header Cockpit */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Briefcase className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                Bureau <span className="text-xs font-normal opacity-60 px-2 py-0.5 bg-white/10 rounded-full">Cockpit Workspace</span>
              </h1>
              <p className="text-sm opacity-75 mt-1">
                Centre de travail à distance. Sélectionnez un dossier pour charger ses outils Google.
              </p>
            </div>
          </div>

          {/* Sélecteur */}
          <div className="relative">
            {!master ? (
              <button
                onClick={() => setShowPicker((v) => !v)}
                className="px-4 py-2.5 rounded-xl bg-white text-slate-800 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-100 shadow"
                data-testid="bureau-open-picker"
              >
                <Search className="w-4 h-4" /> Sélectionner un dossier
                <ChevronDown className="w-4 h-4 opacity-60" />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur">
                  <div className="text-[11px] opacity-70 uppercase tracking-wide">Dossier chargé</div>
                  <div className="font-semibold">{master.couple_display_name}</div>
                </div>
                <button
                  onClick={() => setShowPicker(true)}
                  className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs"
                  data-testid="bureau-change-master"
                >
                  Changer
                </button>
                <button
                  onClick={clearSelection}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
                  title="Décharger le dossier"
                  data-testid="bureau-clear-master"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Picker dropdown */}
            {showPicker && (
              <div
                className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-30 overflow-hidden text-slate-800"
                data-testid="bureau-picker"
              >
                <div className="p-3 border-b border-slate-100">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                    <input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher un dossier (nom, email)…"
                      className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="bureau-picker-search"
                    />
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {loadingMasters ? (
                    <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div>
                  ) : filteredMasters.length === 0 ? (
                    <div className="p-6 text-sm text-slate-400 text-center">Aucun dossier trouvé</div>
                  ) : (
                    filteredMasters.map((m) => {
                      const expActive = (m.active_expertises || []);
                      return (
                        <button
                          key={m.id}
                          onClick={() => { setSelectedId(m.id); setShowPicker(false); setSearch(""); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 border-b border-slate-50 transition"
                          data-testid={`bureau-picker-item-${m.id}`}
                        >
                          <div className="font-semibold text-sm text-slate-800">{m.couple_display_name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{m.primary_email}</div>
                          <div className="flex gap-1 mt-1">
                            {expActive.includes("fiscal") && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Fiscal</span>
                            )}
                            {expActive.includes("immeuble") && (
                              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Immeuble</span>
                            )}
                            {expActive.length === 0 && (
                              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Aucune expertise active</span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Corps */}
      {!master ? (
        <EmptyState />
      ) : busy && !hub ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>
      ) : (
        <Cockpit
          master={master}
          activeExp={activeExp}
          setActiveExp={setActiveExp}
          expertiseTabs={expertiseTabs}
          hub={hub}
          busy={busy}
          onCreateCalendar={createCalendarManually}
        />
      )}
    </div>
  );
}
